<script setup>
import { nextTick, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import useAccountStore from '@organisms/ubiq-administration/store/account';
import { trans } from '../../../../_00_elements/translation';
import HorizontalStepper from '../../../../_02_molecules/components/Steppers/HorizontalStepper.vue';
import ChooseCustomer from './ChooseCustomer.vue';
import ChooseOffice from './ChooseOffice.vue';
import InfosCustomer from './InfosCustomer.vue';
import InfosSubscription from './InfosSubscription.vue';
import SummarySubscription from './Summary.vue';
import useSubscriptionStore from '../../store/subscriptions';
import ConfirmationSigning from './ConfirmationSigning.vue';
import AmountSubscription from './AmountSubscription.vue';
import eventBus from '../../../../_00_elements/tools/event-bus';

const router = useRouter();
const route = useRoute();

const accountStore = useAccountStore();
const subscriptionStore = useSubscriptionStore();

const currentStep = ref(0);
const isConfirmationDisplayed = ref(true);
const stepsEl = ref(null);
const stepTop = ref(null);

const scrollToTop = async () => {
  await nextTick();
  requestAnimationFrame(() => {
    if (stepTop.value?.scrollIntoView) {
      stepTop.value.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  });
};

const nextStep = async () => {
  ++currentStep.value;
  await nextTick();
  eventBus.dispatchEvent(new CustomEvent('sausage-step-changed'));
  scrollToTop();
};

const steps = [ 1, 2, 3, 4, 5, 6 ];

const closeCreation = () => {
  router.push({ name: 'subscriptions' });
};

const goBack = async () => {
  if (currentStep.value === 0) {
    closeCreation();
    return;
  }
  --currentStep.value;
  await nextTick();
  eventBus.dispatchEvent(new CustomEvent('sausage-step-changed'));
  scrollToTop();
};

onMounted(() => {
  accountStore.setPayOrganizationSeller(window.initialState.relatedOrganization);
  if (!accountStore.payOrganizationSeller?.has_kyc_active === true) {
    router.push({ name: 'subscriptions' });
    return;
  }

  subscriptionStore.$reset();
  const dealId = parseInt(route.query.deal, 10);
  if (dealId) {
    subscriptionStore.setPreselectedDeal(dealId);
    subscriptionStore.setSelectedValuesList('customer', dealId);
  }
});
</script>

<template>
  <div class="subscription-manager">
    <div v-if="isConfirmationDisplayed" class="subscriptions__information">
      <ConfirmationSigning @next-step="isConfirmationDisplayed = false" />
    </div>
    <div v-if="!isConfirmationDisplayed" ref="stepsEl" class="subscriptions__steps">
      <span ref="stepTop" aria-hidden="true" />
      <HorizontalStepper
        class="gs-stepper__horizontal"
        :steps="steps"
        :title="trans('messaging.decision.stripe.subscription.create')"
        :actual-step="currentStep"
        stepper-type="guided-search"
        :icon-info="false"
      />
      <ChooseCustomer v-if="currentStep === 0" @next-step="nextStep" />
      <ChooseOffice v-if="currentStep === 1" @next-step="nextStep" />
      <InfosCustomer
        v-if="currentStep === 2"
        :email="subscriptionStore.email"
        :phone="subscriptionStore.phone"
        @next-step="nextStep"
      />
      <AmountSubscription
        v-if="currentStep === 3"
        :amount="subscriptionStore.amount"
        @next-step="nextStep"
      />
      <InfosSubscription
        v-if="currentStep === 4"
        :start-date="subscriptionStore.startDate"
        :end-date="subscriptionStore.endDate"
        @next-step="nextStep"
      />
      <SummarySubscription v-if="currentStep === 5" @next-step="closeCreation" />
      <div class="subscriptions__back" @click="goBack">
        {{ trans('global.return') }}
      </div>
    </div>
  </div>
</template>
